import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Search } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { Translate, getTranslation } from "orion-components/i18n";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import ReminderDialog from "./components/ReminderDialog";
import Card from "./components/Card";
import { deleteReminders } from "./reminders/actions";
import { closeBulkNotifications } from "./notifications/actions";
import { getDir } from "orion-components/i18n/Config/selector";

const propTypes = {
    componentState: PropTypes.object
};

const defaultProps = {
    componentState: {}
};

const useStyles = makeStyles({
    formControlLabel: {
        fontSize: 10
    }
});

const NotificationsCenter = ({ componentState }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const notifications = useSelector((state) => Object.values(state.globalData?.notifications?.activeItemsById) || []);
    const reminders = useSelector((state) => state.globalData?.reminders || {});
    const dir = useSelector((state) => getDir(state));
    const scrollBarsEnabled = useSelector((state) => getScrollBarAppState(state));

    const [searchString, setSearchString] = useState("");
    const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
    const [editReminderPanel, setEditReminderPanel] = useState(false);
    const [reminderToEdit, setReminderToEdit] = useState(null);
    const [selectedReminders, setSelectedReminders] = useState([]);

    const data = (editReminderPanel ? [...reminders] : [...notifications])
        ?.toSorted((a, b) => new Date(b.createdDate || b.dueDate) - new Date(a.createdDate || a.dueDate));
    const filteredData = !isEmpty(searchString)
        ? data?.filter((item) => (item.title || item.name || item.subject).toLowerCase().includes(searchString.toLowerCase()))
        : data;
    const selectAll = reminders.length === 0 || reminders?.find((item) => !selectedReminders.includes(item.id));

    const searchNotification = (e) => {
        setSearchString(e.target.value);
    };

    const editReminder = (reminder) => {
        setReminderToEdit(reminder);
        setReminderDialogOpen(true);
    };

    const selectAllReminders = (e) => {
        const checked = e.target.checked;
        const reminderIds = checked ? filteredData?.filter((item) => item?.displayType)?.map((item) => item.id) : [];
        setSelectedReminders(reminderIds);
    };

    const closeDialog = () => {
        setReminderDialogOpen(false);
        setReminderToEdit(null);
    };

    const styles = {
        headerAdornment: {
            fontSize: 10,
            marginBottom: 12
        },
        checkbox: {
            color: "#B7BABF",
            ...(dir === "rtl" ? { paddingRight: 0 } : { paddingLeft: 0 }),
            "&.Mui-checked": {
                color: "#1688bd!important"
            }
        },
        formControlLabel: {
            margin: 0,
            color: "#fff",
            ...(dir === "rtl" ? { marginLeft: "auto" } : { marginRight: "auto" })
        }
    };

    return (
        <div
            id="notification-tab-wrapper"
            style={{ overflow: scrollBarsEnabled ? "hidden" : "auto" }}
            className={"cf" + (componentState.hasError ? " adjusted" : "")}
        >
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    marginBottom: "20px",
                    padding: "0 12px 12px",
                    alignItems: "center",
                    height: 61
                }}
            >
                <div className="search-field" style={{ width: "100%", display: "flex" }}>
                    <TextField
                        id="search-field"
                        placeholder={getTranslation("global.dock.notifications.main.search")}
                        onChange={searchNotification}
                        variant="standard"
                        style={{
                            backgroundColor: "transparent",
                            width: "90%"
                        }}
                        sx={{
                            "& .MuiInputBase-input": {
                                height: "2.15rem !important"
                            },
                            "& input:-webkit-autofill": {
                                boxShadow: "0 0 0 100px #2C2C2C inset",
                                WebkitBoxShadow: "0 0 0 100px #2C2C2C inset",
                                WebkitTextFillColor: "#6C6C6C"
                            }
                        }}
                    />
                    <IconButton style={{ margin: "10px 0px 0px 10px" }}>
                        <Search style={{ color: "6C6C6C", width: 24, height: 24 }} />
                    </IconButton>
                </div>
            </div>

            <Button
                variant="contained"
                color="primary"
                style={{
                    width: "93%",
                    margin: 12,
                    color: "#fff",
                    backgroundColor: "#6EB4ED",
                    height: 35
                }}
                sx={{
                    "&:hover": {
                        backgroundColor: "#3076AF !important"
                    }
                }}
                onClick={() => setReminderDialogOpen(true)}
            >
                <Translate value={"global.dock.notificationsCenter.addReminder"} />
            </Button>

            <div style={{ height: "100%", overflow: "hidden" }}>
                <div style={{ padding: "0rem 0.8rem 0.8rem 0.8rem" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        {editReminderPanel ? (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!selectAll}
                                            onChange={selectAllReminders}
                                            color="primary"
                                            sx={styles.checkbox}
                                        />
                                    }
                                    label={getTranslation("global.dock.notificationsCenter.selectAll")}
                                    style={styles.formControlLabel}
                                    classes={{ label: classes.formControlLabel }}
                                />
                                <a
                                    onClick={() => {
                                        setSelectedReminders([]);
                                        setEditReminderPanel(false)
                                    }}
                                    className="cb-font-b2 cb-font-link"
                                    style={{ ...styles.headerAdornment, margin: "0px 10px 12px", color: "#666C74" }}
                                >
                                    <Translate value="global.dock.notificationsCenter.cancel" />
                                </a>
                                <a
                                    onClick={() => selectedReminders?.length && dispatch(deleteReminders(selectedReminders))}
                                    className={`cb-font-b2 cb-font-link ${selectedReminders?.length ? "" : "disabled"}`}
                                    style={{ ...styles.headerAdornment, color: "#D3615C" }}
                                >
                                    <Translate value="global.dock.notificationsCenter.deleteSelected" />
                                </a>
                            </>
                        ) : (
                            <>
                                <a
                                    onClick={() => setEditReminderPanel(true)}
                                    className="cb-font-b2 cb-font-link"
                                    style={{ ...styles.headerAdornment, margin: "0px 10px 12px" }}
                                >
                                    <Translate value="global.dock.notificationsCenter.manageReminders" />
                                </a>
                                {filteredData?.length ?
                                    <a
                                        onClick={() => dispatch(closeBulkNotifications(notifications?.map((notification) => notification?.id)))}
                                        className="cb-font-b2 cb-font-link"
                                        style={styles.headerAdornment}
                                    >
                                        <Translate value="global.dock.notificationsCenter.dismissAll" />
                                    </a> : <></>
                                }
                            </>
                        )}
                    </div>
                    {filteredData?.length
                        ? filteredData
                            .filter((item) => editReminderPanel ? item?.displayType : true)
                            .map((item) => {
                                return <Card
                                    dir={dir}
                                    item={item}
                                    editMode={editReminderPanel}
                                    editReminder={editReminder}
                                    selectedReminders={selectedReminders}
                                    setSelectedReminders={setSelectedReminders}
                                    customStyles={{ marginBottom: 10 }}
                                />
                            })
                        :
                        <Typography
                            style={{
                                padding: "24px 0px",
                                color: "#B5B9BE",
                                display: "inherit",
                                border: "1px solid #707070",
                                borderRadius: "10px",
                                height: "143px",
                                margin: "23px 48px",
                                display: "flex",
                                alignItems: "center"
                            }}
                            align="center"
                            variant="caption"
                            sx={{ color: "#98999D" }}
                        >
                            <Translate
                                value={`global.dock.notifications.main.${editReminderPanel ? "noReminders" : "noNotifications"}`}
                                style={{ width: "100%", fontSize: 14, color: "#B4B8BC" }}
                            />
                        </Typography>
                    }
                </div>
            </div >
            <ReminderDialog
                key={reminderToEdit ? reminderToEdit.id : "new-reminder"}
                open={reminderDialogOpen}
                closeDialog={closeDialog}
                dir={dir}
                reminder={reminderToEdit}
            />
        </div >
    );
};

NotificationsCenter.propTypes = propTypes;
NotificationsCenter.defaultProps = defaultProps;

export default NotificationsCenter;
